import * as React from 'react';
import Box from '@mui/material/Box';
import { columns, generateRows } from './partials';
import { BillingAccountSelector, Table, CreateNegotiatorAccount } from '@organisms';
import TextView from '../../../../../atoms/textView';
import { Alert, Button } from '@atoms';
import { DateTime } from 'luxon';
import {
  useCompaniesDetailedList,
  callSignOutAllNegotiatorUsers,
  callSignOutCompanyUsers
} from '@api';
import { useState, useContext } from 'react';
import { routes, TableContext } from '@particles';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Stack,
  Typography
} from '@mui/material';
import { navigate } from '@reach/router';
import { toast } from 'react-toastify';

interface NegotiatorTableProps {
  start: Date;
  end: Date;
}

const NegotiatorTable: React.FC<NegotiatorTableProps> = (props: NegotiatorTableProps) => {
  const { start, end } = props;

  const {
    state: {
      accountPerformancePagination: { page, perPage, orderBy }
    }
  } = useContext(TableContext);

  const startDate = DateTime.fromJSDate(start).toISO({
    suppressMilliseconds: true,
    includeOffset: false
  });
  const endDate = DateTime.fromJSDate(end).toISO({
    suppressMilliseconds: true,
    includeOffset: false
  });

  const { data: response, isFetching: loading } = useCompaniesDetailedList({
    from: startDate,
    to: endDate,
    pageNumber: page,
    pageSize: perPage,
    sortBy: orderBy
  });
  const accountPerformance = response?.data;

  const [signoutAllNegotiator, setSignoutAllNegotiator] = useState(false);
  const [signoutLoading, setSignoutLoading] = useState(false);
  const [signoutError, setSignoutError] = useState<string>();
  const [signoutTarget, setSignoutTarget] = useState<NegotiatorDetailed | undefined>();

  const onAction = (item: NegotiatorDetailed, type: 'signout') => {
    if (type === 'signout') {
      setSignoutTarget(item);
    }
  };

  const signOutCompanyApi = async () => {
    if (!signoutTarget) return;
    setSignoutLoading(true);
    try {
      const { code, error } = await callSignOutCompanyUsers(signoutTarget.id);
      if (code >= 400 && error) {
        setSignoutError(error.message);
        setTimeout(() => {
          setSignoutError(undefined);
          setSignoutLoading(false);
        }, 3000);
      } else {
        setSignoutLoading(false);
        setSignoutTarget(undefined);
        setSignoutError(undefined);
      }
    } catch {
      setSignoutError('Failed to sign out users');
      setSignoutLoading(false);
      setTimeout(() => setSignoutError(undefined), 3000);
    }
  };

  const signOutAllNegotiatorApi = async () => {
    setSignoutLoading(true);
    try {
      const { code, error } = await callSignOutAllNegotiatorUsers();
      if (code >= 400 && error) {
        setSignoutError(error.message);
        setTimeout(() => {
          setSignoutError(undefined);
          setSignoutLoading(false);
        }, 3000);
      } else {
        setSignoutLoading(false);
        setSignoutAllNegotiator(false);
        setSignoutError(undefined);
        toast.info('Sign out job queued. You will be notified when it completes.');
      }
    } catch {
      setSignoutError('Failed to sign out all negotiator users');
      setSignoutLoading(false);
      setTimeout(() => setSignoutError(undefined), 3000);
    }
  };

  const rowData: TableRow[] = accountPerformance?.data
    ? generateRows(accountPerformance.data, onAction)
    : [];

  return (
    <Box>
      <Grid container justifyContent="space-between" alignItems="center" mb={2} mt={3}>
        <Grid item>
          <TextView $fontSize={30}>All Accounts</TextView>
        </Grid>
        <Grid item>
          <Stack direction="row" spacing={1} alignItems="center">
            <CreateNegotiatorAccount />
            <Button onClick={() => setSignoutAllNegotiator(true)}>Sign Out All Negotiator</Button>
            <BillingAccountSelector
              type="n"
              status="live"
              caller="negotiator-performance"
              goHref={(item) => `${routes.SA_NegotiatorDetails}/${item.reapitCompanyId}`}
            />
          </Stack>
        </Grid>
      </Grid>
      <Table
        name="account_performance"
        columns={columns}
        data={rowData}
        isLoading={loading}
        pagination={accountPerformance}
        persistColumnPrefs
        // error={error}
        // selectable
      />

      <Dialog
        open={signoutAllNegotiator}
        onClose={() => setSignoutAllNegotiator(false)}
        maxWidth="sm"
        fullWidth>
        <DialogTitle>Sign Out All Negotiator Users</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            {signoutError && <Alert message={signoutError} severity="error" />}
            <Typography variant="body1">
              Are you sure you want to sign out all users across{' '}
              <strong>every negotiator location</strong>?
            </Typography>
            <Alert
              message="This will revoke all active sessions and force every negotiator user to sign in again. This action cannot be undone."
              severity="warning"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSignoutAllNegotiator(false)}>Cancel</Button>
          <Button onClick={signOutAllNegotiatorApi} disabled={signoutLoading}>
            Sign Out All
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!signoutTarget}
        onClose={() => setSignoutTarget(undefined)}
        maxWidth="sm"
        fullWidth>
        <DialogTitle>Sign Out All Users</DialogTitle>
        <DialogContent>
          {signoutTarget && (
            <Stack spacing={2} mt={1}>
              {signoutError && <Alert message={signoutError} severity="error" />}
              <Typography variant="body1">
                Are you sure you want to sign out all users of{' '}
                <strong>{signoutTarget.name}</strong>?
              </Typography>
              <Alert
                message="This will revoke all active sessions and force every user in this account to sign in again."
                severity="warning"
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSignoutTarget(undefined)}>Cancel</Button>
          <Button onClick={signOutCompanyApi} disabled={signoutLoading}>
            Sign Out All
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default NegotiatorTable;
